// src/features/chat/WorkspacePeopleGrid.tsx
// Empty-chat state: instead of "No conversations yet", the people you could
// talk to — avatar cards with a quick Message action that opens (creating if
// needed) the DM. Loads once via the privacy-aware profile search; failure is
// a retry card, never a crash, because an empty workspace is not an error.

import { useEffect, useState } from 'react';
import { useI18n } from '@react/hooks';
import { ErrorAlert } from '@react/components';
import type { PickerCandidate } from '@/features/userprofile/pickerCandidates';
import { loadWorkspacePeople } from './logic';
import { EntityAvatar } from '@/components/EntityAvatar';
import { useOpenDirectConversation } from './useOpenDirectConversation';

const PEOPLE_LIMIT = 12;

export function WorkspacePeopleGrid({ currentProfileId }: { currentProfileId: string | null }) {
  const { t } = useI18n();
  const [people, setPeople] = useState<PickerCandidate[] | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [nonce, setNonce] = useState(0);
  const dm = useOpenDirectConversation();

  useEffect(() => {
    let cancelled = false;
    setLoadFailed(false);
    loadWorkspacePeople(PEOPLE_LIMIT)
      .then((results) => {
        if (cancelled) return;
        setPeople(results.filter(p => p.profileId && p.profileId !== currentProfileId));
      })
      .catch(() => {
        if (!cancelled) setLoadFailed(true);
      });
    return () => { cancelled = true; };
  }, [currentProfileId, nonce]);

  if (loadFailed) {
    return (
      <div className="px-4 py-12 text-center">
        <p className="text-sm text-muted-foreground">{t('chat.people.loadFailed')}</p>
        <button
          type="button"
          onClick={() => setNonce(n => n + 1)}
          className="mt-2 text-xs font-semibold text-primary transition-transform hover:underline active:scale-[0.96]"
        >
          {t('common.retry')}
        </button>
      </div>
    );
  }

  // Skeleton first — a blank pane that fills in later reads as a glitch.
  if (people === null) {
    return (
      <div className="p-3">
        <div className="mb-3 h-3 w-40 animate-pulse rounded bg-muted" />
        <div className="grid grid-cols-2 gap-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="flex flex-col items-center gap-2 rounded-lg border border-border p-3">
              <div className="h-10 w-10 animate-pulse rounded-full bg-muted" />
              <div className="h-3 w-16 animate-pulse rounded bg-muted" />
              <div className="h-6 w-14 animate-pulse rounded-md bg-muted" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (people.length === 0) {
    return (
      <div className="px-4 py-12 text-center">
        <p className="text-sm font-medium text-foreground">{t('chat.noItems')}</p>
      </div>
    );
  }

  return (
    <div className="p-3">
      <p className="mb-2 px-1 text-xs font-medium uppercase tracking-wider text-muted-foreground">
        {t('chat.people.title')}
      </p>

      {dm.error ? (
        <div className="mb-2">
          <ErrorAlert error={dm.error} dismissible onDismiss={dm.clearError} />
        </div>
      ) : null}

      <ul className="grid grid-cols-2 gap-2">
        {people.map((person, i) => (
          <li
            key={person.profileId}
            className="animate-in fade-slide-in"
            style={{ animationDelay: `${Math.min(i, 8) * 60}ms` }}
          >
            <div className="group flex h-full flex-col items-center gap-1.5 rounded-lg border border-border bg-card p-3 text-center transition-colors hover:border-primary/40 hover:bg-muted/40">
              <EntityAvatar name={person.displayName} image={person.avatar} profileId={person.profileId} size="md" className="h-10 w-10" />
              <span className="w-full min-w-0">
                <span className="block truncate text-sm font-medium text-foreground">{person.displayName}</span>
                {person.secondary && (
                  <span className="block truncate text-xs text-muted-foreground">{person.secondary}</span>
                )}
              </span>
              <button
                type="button"
                disabled={dm.isOpening}
                onClick={() => dm.open(person.profileId)}
                className="mt-0.5 inline-flex h-7 items-center justify-center gap-1.5 rounded-md bg-primary px-2.5 text-xs font-semibold text-primary-foreground transition-[transform,background-color] hover:bg-primary/90 active:scale-[0.96] disabled:opacity-60"
              >
                {dm.pendingProfileId === person.profileId ? (
                  <span className="h-3 w-3 animate-spin rounded-full border-2 border-primary-foreground border-t-transparent" />
                ) : null}
                {t('chat.people.message')}
              </button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default WorkspacePeopleGrid;
